import { useEffect, useMemo, useState } from "react";
import { COUNTRIES, getHostel, getTrip, HOSTEL_SLUGS, HOSTELS } from "./content";
import LiveMap from "./LiveMap";
import type { MadMonkeyMapProps } from "./MadMonkeyMap";
import { FILTER_ALL, FILTER_ROUTE, hostelMarker, hostelPoint, poiMarkers, visibleHostels, type MapFilter, type PoiMarkerData } from "./mapModel";
import { HostelCard, PoiCard } from "./PlaceCard";
import Planner from "./Planner";
import { inRoute, stopsOfTrip, toggleStop } from "./plannerLogic";
import { useRoute } from "./routeStore";
import { Flag, MM_MONKEY } from "./ui";
import styles from "./MadMonkeyHome.module.scss";
import shared from "./MadMonkey.module.scss";

type Selection = { type: "hostel"; slug: string } | { type: "poi"; hostel: string; index: number };

interface BuilderProps {
	/** the map's filter and the hostel whose card is open when the page opens, from a "start a route here" link */
	initialFilter?: MapFilter;
	initialHostel?: string | null;
}

/**
 * "Build your own Mad Monkey route": the filters, the live hostel map and the planner. They share one route
 * (the route store): a stop added on the map shows in the planner, a stop dragged in the planner moves the
 * pink line.
 */
const Builder = ({ initialFilter = FILTER_ROUTE, initialHostel = null }: BuilderProps) => {
	const [route, setRoute] = useRoute();
	const [filter, setFilter] = useState<MapFilter>(initialFilter);
	const [selected, setSelected] = useState<Selection | null>(initialHostel ? { type: "hostel", slug: initialHostel } : null);

	const view = useMemo(() => visibleHostels(filter, route), [filter, route]);
	const activePoi = selected?.type === "poi" ? selected : null;

	const hostels = useMemo(
		() =>
			view.shown.map((slug) =>
				hostelMarker(slug, { kind: "stay", inRoute: inRoute(route, slug), active: selected?.type === "hostel" && selected.slug === slug, addable: true })
			),
		[view, route, selected]
	);
	const pois = useMemo(() => view.shown.flatMap((slug) => poiMarkers(slug, activePoi?.hostel === slug ? activePoi.index : -1)), [view, activePoi]);
	const fit = useMemo(() => view.fit.map(hostelPoint), [view]);
	const routeSlugs = useMemo(() => route.map((r) => r.s), [route]);

	useEffect(() => {
		if (!selected) return undefined;
		const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSelected(null);
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [selected]);

	// The map reframes for the new filter, which would leave an open card hanging off the edge of it.
	const chooseFilter = (next: MapFilter) => {
		setFilter(next);
		setSelected(null);
	};

	const toggle = (slug: string) => setRoute((r) => toggleStop(r, slug));

	const loadTrip = (id: string) => {
		const trip = getTrip(id);
		if (!trip) return;
		setRoute(stopsOfTrip(trip));
		setSelected(null);
		setFilter(FILTER_ROUTE);
	};

	let popup: MadMonkeyMapProps["popup"] = null;
	if (selected?.type === "hostel" && view.shown.includes(selected.slug)) {
		const h = HOSTELS[selected.slug];
		popup = {
			lon: h.lon,
			lat: h.lat,
			children: (
				<HostelCard slug={selected.slug} inRoute={inRoute(route, selected.slug)} onToggle={() => toggle(selected.slug)} onClose={() => setSelected(null)} />
			),
		};
	} else if (selected?.type === "poi") {
		const poi = getHostel(selected.hostel)?.pois?.[selected.index];
		if (poi) popup = { lon: poi.lon, lat: poi.lat, children: <PoiCard poi={poi} onClose={() => setSelected(null)} /> };
	}

	const counts = (cc: string) => HOSTEL_SLUGS.filter((s) => HOSTELS[s].cc === cc).length;

	return (
		<section id="hostels" className={styles.builder} aria-labelledby="mm-hostels-h">
			<div className={shared.wrap}>
				<div className={shared.secHead}>
					<span className={shared.eyebrow}>Route builder</span>
					<h2 className={shared.h2} id="mm-hostels-h">
						Build your own <span className={shared.hl}>Mad Monkey route</span>
					</h2>
					<p className={shared.lede}>
						Tap a hostel, add it to your route and watch the line draw itself on the Stippl map. Pick a country to zoom in, or start from one of our routes.
					</p>
				</div>
				<div className={styles.filters} role="group" aria-label="Filter by country">
					<button type="button" id="mm-filter-route" className={styles.yr} aria-pressed={filter === FILTER_ROUTE} onClick={() => chooseFilter(FILTER_ROUTE)}>
						<img src={MM_MONKEY} alt="" />
						Your route <small>{route.length}</small>
					</button>
					<button type="button" id="mm-filter-all" className={styles.all} aria-pressed={filter === FILTER_ALL} onClick={() => chooseFilter(FILTER_ALL)}>
						All <small>{HOSTEL_SLUGS.length}</small>
					</button>
					{Object.keys(COUNTRIES).map((cc) => (
						<button type="button" key={cc} id={`mm-filter-${cc}`} aria-pressed={filter === cc} onClick={() => chooseFilter(cc)}>
							<Flag cc={cc} />
							{COUNTRIES[cc]} <small>{counts(cc)}</small>
						</button>
					))}
				</div>
				<div className={styles.atlas}>
					<div className={styles.atlasMap}>
						<LiveMap
							id="mm-atlas"
							label={
								filter === FILTER_ROUTE && route.length
									? "Map of your Mad Monkey route"
									: `Map of Mad Monkey hostels in ${COUNTRIES[filter] ?? "Southeast Asia"}`
							}
							hostels={hostels}
							pois={pois}
							route={routeSlugs}
							fit={fit}
							popup={popup}
							onHostelSelect={(slug) => setSelected({ type: "hostel", slug })}
							onHostelAdd={toggle}
							onPoiSelect={(poi: PoiMarkerData) => setSelected({ type: "poi", hostel: poi.hostel, index: poi.index })}
						/>
					</div>
					<Planner route={route} setRoute={setRoute} onLoadTrip={loadTrip} />
				</div>
			</div>
		</section>
	);
};

export default Builder;
